import React from 'react';
import { Box, Button, Link } from '@amzn/awsui-components-core';
import { EmployeeCardNodeProps } from '../../types/orgChart.types';
import { ProfilePicture } from '../../../ea-huddle-v2/components/ProfilePicture';
import './EmployeeCardNode.scss';

const PHONETOOL_URL = 'https://phonetool.amazon.com/users';
const ATOZ_PERFORMANCE_URL = 'https://atoz.amazon.work/manager/profiles';
const ATOZ_EMPLOYEE_DETAILS_URL = 'https://atoz.amazon.work/manager/profiles';

const formatEmployeeDetails = (employee: EmployeeCardNodeProps['employee']): string => {
  const formatLocation = (city: string, country: string): string => {
    if (city && country) {
      return `${city}, ${country}`;
    }
    return city || country || '';
  };

  const parts = [
    employee.department,
    employee.jobTitle,
    formatLocation(employee.city, employee.country),
    employee.jobLevel ? `L${employee.jobLevel}` : null
  ].filter(Boolean);

  return parts.join(' • ');
};

export const EmployeeCardNode: React.FC<EmployeeCardNodeProps> = ({
  employee,
  isExpanded,
  isLeader,
  depth,
  hasChildren,
  onExpand,
  onCollapse,
  onRemove,
  onClick
}) => {
  const displayName = `${employee.firstName} ${employee.lastName}`.trim();
  const employeeDetails = formatEmployeeDetails(employee);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const handleExpandCollapse = (event: CustomEvent) => {
    event.stopPropagation();
    if (isExpanded) {
      onCollapse();
    } else {
      onExpand();
    }
  };

  const handleRemove = (event: CustomEvent) => {
    event.stopPropagation();
    onRemove();
  };

  // Open phonetool profile in new tab
  const handleNameClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${PHONETOOL_URL}/${employee.login}`, '_blank', 'noopener,noreferrer');
  };

  // Open AtoZ performance page in new tab
  const handlePerformanceClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${ATOZ_PERFORMANCE_URL}/${employee.login}/performance`, '_blank', 'noopener,noreferrer');
  };

  // Open AtoZ employee details page in new tab
  const handleEmployeeDetailsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${ATOZ_EMPLOYEE_DETAILS_URL}/${employee.login}/employee-details`, '_blank', 'noopener,noreferrer');
  };

  const containerClassName = ['employee-card-node', onClick ? 'employee-card-node--clickable' : '', isLeader ? 'employee-card-node--leader' : '']
    .filter(Boolean)
    .join(' ');

  // Calculate indentation based on depth (24px per level)
  const indentStyle = {
    paddingLeft: `${depth * 24}px`
  };

  return (
    <div
      className={containerClassName}
      style={indentStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
    >
      {/* Expand/Collapse Button */}
      <div className="employee-card-node__expand-area">
        {hasChildren ? (
          <Button
            variant="icon"
            iconName={isExpanded ? 'angle-down' : 'angle-right'}
            onClick={handleExpandCollapse}
            ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
          />
        ) : (
          <div className="employee-card-node__expand-placeholder" />
        )}
      </div>

      {/* Employee Info */}
      <div className="employee-card-node__info">
        <ProfilePicture loginName={employee.login} name={displayName} size="small" />
        <div className="employee-card-node__details">
          <div className="employee-card-node__name-line">
            <Link
              href={`${PHONETOOL_URL}/${employee.login}`}
              onFollow={(e) => {
                e.preventDefault();
                handleNameClick(e as unknown as React.MouseEvent);
              }}
            >
              <span className="employee-card-node__name">{displayName}</span>
            </Link>
            {employee.email && (
              <Link
                href={`mailto:${employee.email}`}
                onFollow={(e) => {
                  e.preventDefault();
                  window.location.href = `mailto:${employee.email}`;
                }}
              >
                <span className="employee-card-node__name">({employee.email})</span>
              </Link>
            )}
          </div>
          {employeeDetails && (
            <Box variant="small" color="text-body-secondary">
              <span className="employee-card-node__subtitle" title={employeeDetails}>
                {employeeDetails}
              </span>
            </Box>
          )}
        </div>
      </div>

      {/* Actions - Performance, Employee Details links */}
      <div className="employee-card-node__actions">
        <Link
          href={`${ATOZ_PERFORMANCE_URL}/${employee.login}/performance`}
          fontSize="body-s"
          onFollow={(e) => {
            e.preventDefault();
            handlePerformanceClick(e as unknown as React.MouseEvent);
          }}
        >
          Performance
        </Link>
        <Link
          href={`${ATOZ_EMPLOYEE_DETAILS_URL}/${employee.login}/employee-details`}
          fontSize="body-s"
          onFollow={(e) => {
            e.preventDefault();
            handleEmployeeDetailsClick(e as unknown as React.MouseEvent);
          }}
        >
          Employee Details
        </Link>
      </div>

      {/* Remove button - hidden for leader */}
      <div className="employee-card-node__remove">
        {!isLeader && <Button variant="icon" iconName="remove" onClick={handleRemove} ariaLabel={`Remove ${displayName}`} />}
      </div>
    </div>
  );
};

export default EmployeeCardNode;
